import { useState } from 'react';
import type { ReactNode } from 'react';
import { TenantStatus } from '@yourstore/domain';
import { useAction, useApi } from '../context';
import { Confirm, Failure } from '../bits';
import { Menu, useToast } from '../kit';
import type { MenuItem } from '../kit';
import { stageMove, stageName } from '../../vocabulary';
import { useT } from '../../messages';

/**
 * **"Change stage"** — the lifecycle's moves, as one menu in the business
 * header (console redesign, phase A). It replaces the Lifecycle card's row of
 * four "Move to …" buttons, which gave `paused` the same weight as `live` and
 * named each by its wire token.
 *
 * What did not change is the rule the card was built on. Every move is
 * confirmed, and the confirm quotes the consequence rather than asking whether
 * the operator is sure — now in plain words (`vocabulary.ts`'s `stageMove`),
 * but the same consequences: going live is **the cutover**, from which moment
 * partners are told times are exact and will promise them to clients.
 *
 * The no-op is still not offered: `admin-api` answers `409` on a move that
 * changes nothing — deliberately, because a no-op is either a second operator
 * having already done it or a console pointed at the wrong environment — so the
 * current stage is left out of the menu and the refusal stays a backstop.
 *
 * **Offboarding** is at the bottom, in danger style, behind a rule. The plan
 * moves it to a danger zone on the Setup tab (phase B2); until that card
 * exists it stays here, so no function is lost on the way (principle 4).
 *
 * Forward moves confirm with the ordinary primary button; pausing and
 * offboarding with the red one. Red on every move would teach an operator that
 * red means nothing.
 */
const ORDER: readonly TenantStatus[] = ['onboarding', 'importing', 'live', 'paused', 'offboarded'];

export function useStageMenu({
  tenantId,
  name,
  status,
  onChanged,
}: {
  tenantId: string;
  name: string;
  status: TenantStatus;
  onChanged: () => void;
}): { menu: ReactNode; notice: ReactNode } {
  const api = useApi();
  const t = useT();
  const toast = useToast();
  const action = useAction();
  const [pending, setPending] = useState<TenantStatus | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const confirm = async (): Promise<void> => {
    if (pending === null) return;
    const ok = await action.run(async () => {
      const answer = await api.setStatus(tenantId, pending);
      const stage = stageName(t, answer.status);
      setResult(t('stage.moved', { name, stage, seconds: answer.effective_within_seconds }));
      toast(t('stage.toast', { name, stage }));
    });
    if (ok) {
      setPending(null);
      onChanged();
    }
  };

  const items: MenuItem[] = [];
  // Every status the domain knows, in the journey's order; a status this
  // console has no words for is left out rather than offered as a token.
  for (const next of ORDER.filter((s) => TenantStatus.options.includes(s))) {
    if (next === status) continue;
    const move = stageMove(t, next, name);
    if (move === null) continue;
    items.push({
      key: next,
      label: move.label,
      hint: move.meaning,
      separator: next === 'paused' || next === 'offboarded',
      tone: next === 'offboarded' ? 'danger' : undefined,
      onPick: () => {
        action.clear();
        setResult(null);
        setPending(next);
      },
    });
  }

  const move = pending === null ? null : stageMove(t, pending, name);

  return {
    menu: (
      <>
        <Menu label={t('stage.menu')} items={items} />
        {pending !== null && move !== null ? (
          <Confirm
            title={move.title}
            consequence={move.consequence}
            confirmLabel={move.confirm}
            tone={pending === 'paused' || pending === 'offboarded' ? 'danger' : 'primary'}
            busy={action.busy}
            failure={action.failure}
            onConfirm={() => void confirm()}
            onCancel={() => {
              action.clear();
              setPending(null);
            }}
          />
        ) : null}
      </>
    ),
    notice: (
      <>
        {result !== null ? <p className="ok">{result}</p> : null}
        {pending === null && action.failure !== null ? <Failure failure={action.failure} /> : null}
      </>
    ),
  };
}
